import React, { useState, useEffect } from 'react';
import BackButton from '@/components/BackButton';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Plus, Trash, ClockClockwise, ArrowLeft, ArrowRight } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

const REASON_OPTIONS = [
  { value: 'forgot', label_ar: 'نسيان البصمة', label_en: 'Forgot to clock' },
  { value: 'technical_issue', label_ar: 'عطل تقني', label_en: 'Technical issue' },
  { value: 'work_errand', label_ar: 'خروج لمهمة عمل', label_en: 'Work errand' },
  { value: 'emergency', label_ar: 'ظرف طارئ', label_en: 'Emergency' },
  { value: 'other', label_ar: 'أخرى', label_en: 'Other' },
];

const TYPE_LABEL = {
  clock_in:  { ar: 'تصحيح حضور',  en: 'Clock-in' },
  clock_out: { ar: 'تصحيح انصراف', en: 'Clock-out' },
  full_day:  { ar: 'إضافة يوم كامل', en: 'Full Day' },
};

const STATUS_BADGE = {
  pending:   { ar: 'قيد المراجعة', en: 'Pending',   cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  approved:  { ar: 'مقبول',        en: 'Approved',  cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected:  { ar: 'مرفوض',        en: 'Rejected',  cls: 'bg-red-100 text-red-800 border-red-200' },
  cancelled: { ar: 'ملغي',         en: 'Cancelled', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
};

function todayLocal() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}
function daysAgoLocal(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

export default function EmployeeAttendanceCorrections() {
  const navigate = useNavigate();
  const { language, direction } = useLanguage();
  const { user } = useAuth();
  const isAr = language === 'ar';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [form, setForm] = useState({
    target_date: todayLocal(),
    correction_type: 'clock_in',
    requested_clock_in: '',
    requested_clock_out: '',
    reason_code: 'forgot',
    reason_text: '',
  });

  const fetchRows = async () => {
    try {
      const { data } = await axios.get(`${API}/attendance-corrections/my`, { withCredentials: true });
      setRows(data);
    } catch (e) {
      toast.error(isAr ? 'تعذّر جلب الطلبات' : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); }, []);

  const handleSubmit = async () => {
    if (!form.reason_text || form.reason_text.trim().length < 5) {
      toast.error(isAr ? 'يرجى كتابة شرح واضح للسبب (5 أحرف على الأقل)' : 'Reason must be at least 5 characters');
      return;
    }
    if (form.correction_type === 'clock_in' && !form.requested_clock_in) {
      toast.error(isAr ? 'حدد وقت الحضور المطلوب' : 'Specify clock-in time'); return;
    }
    if (form.correction_type === 'clock_out' && !form.requested_clock_out) {
      toast.error(isAr ? 'حدد وقت الانصراف المطلوب' : 'Specify clock-out time'); return;
    }
    if (form.correction_type === 'full_day' && (!form.requested_clock_in || !form.requested_clock_out)) {
      toast.error(isAr ? 'حدد وقت الحضور والانصراف' : 'Specify both clock-in & clock-out'); return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Strip unused time fields
      if (form.correction_type === 'clock_in') payload.requested_clock_out = null;
      if (form.correction_type === 'clock_out') payload.requested_clock_in = null;
      await axios.post(`${API}/attendance-corrections`, payload, { withCredentials: true });
      toast.success(isAr ? 'تم تقديم الطلب بنجاح' : 'Request submitted');
      setOpenNew(false);
      setForm({
        target_date: todayLocal(), correction_type: 'clock_in',
        requested_clock_in: '', requested_clock_out: '',
        reason_code: 'forgot', reason_text: '',
      });
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'تعذّر تقديم الطلب' : 'Submission failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm(isAr ? 'هل تريد إلغاء هذا الطلب؟' : 'Cancel this request?')) return;
    try {
      await axios.delete(`${API}/attendance-corrections/${id}`, { withCredentials: true });
      toast.success(isAr ? 'تم الإلغاء' : 'Cancelled');
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'تعذّر الإلغاء' : 'Cancel failed'));
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F0FA]" dir={direction} data-testid="employee-corrections-page">
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] dark:border-slate-700 py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-semibold text-[#655dc6]">
              {isAr ? 'طلبات تصحيح البصمة' : 'Attendance Correction Requests'}
            </h1>
            <p className="text-sm text-[#655dc6]/70 mt-1">
              {isAr
                ? 'يمكنك طلب تصحيح بصمة لآخر 7 أيام، بحد أقصى 3 طلبات شهرياً. يُعتمد الطلب من مدير النظام.'
                : 'You can request a correction for the last 7 days, up to 3 per month. Approved by the System Administrator.'}
            </p>
          </div>
          <Button onClick={() => setOpenNew(true)} className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2" data-testid="new-correction-btn">
            <Plus size={18} />
            {isAr ? 'طلب جديد' : 'New Request'}
          </Button>
        </div>

        <Card className="border border-[#C4BFF0]">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-[#655dc6] flex items-center gap-2">
              <ClockClockwise size={22} weight="duotone" />
              {isAr ? 'سجل طلباتي' : 'My Requests'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
              </div>
            ) : (
              <div className="overflow-x-auto scrollbar-thin" data-mobile-hint="scroll-x">
                <Table data-testid="corrections-table">
                  <TableHeader>
                    <TableRow className="border-[#C4BFF0]">
                      <TableHead className="text-start">{isAr ? 'تاريخ التصحيح' : 'Target Date'}</TableHead>
                      <TableHead className="text-start">{isAr ? 'النوع' : 'Type'}</TableHead>
                      <TableHead className="text-start">{isAr ? 'الأوقات' : 'Times'}</TableHead>
                      <TableHead className="text-start">{isAr ? 'السبب' : 'Reason'}</TableHead>
                      <TableHead className="text-start">{isAr ? 'الحالة' : 'Status'}</TableHead>
                      <TableHead className="text-start">{isAr ? 'إجراء' : 'Action'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#655dc6]/70">
                        {isAr ? 'لا توجد طلبات سابقة' : 'No requests yet'}
                      </TableCell></TableRow>
                    ) : rows.map((r) => {
                      const s = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
                      const tl = TYPE_LABEL[r.correction_type] || { ar: r.correction_type, en: r.correction_type };
                      return (
                        <TableRow key={r.id} className="border-[#C4BFF0]">
                          <TableCell className="font-medium text-[#655dc6]">{r.target_date}</TableCell>
                          <TableCell className="text-[#655dc6]/80">{isAr ? tl.ar : tl.en}</TableCell>
                          <TableCell className="text-[#655dc6]/70 text-xs">
                            {r.requested_clock_in && <div>{isAr ? 'حضور' : 'In'}: {r.requested_clock_in}</div>}
                            {r.requested_clock_out && <div>{isAr ? 'انصراف' : 'Out'}: {r.requested_clock_out}</div>}
                          </TableCell>
                          <TableCell className="text-[#655dc6]/70 text-xs max-w-xs">
                            <div className="font-semibold">
                              {(REASON_OPTIONS.find(o => o.value === r.reason_code) || {})[isAr ? 'label_ar' : 'label_en'] || r.reason_code}
                            </div>
                            <div className="text-[10px] opacity-70 truncate">{r.reason_text}</div>
                            {r.status === 'rejected' && r.rejection_reason && (
                              <div className="text-[10px] text-red-600 mt-1">
                                {isAr ? 'سبب الرفض' : 'Rejection'}: {r.rejection_reason}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={s.cls}>{isAr ? s.ar : s.en}</Badge>
                          </TableCell>
                          <TableCell>
                            {r.status === 'pending' && (
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => handleCancel(r.id)}
                                className="text-red-600 hover:bg-red-50 gap-1"
                                data-testid={`cancel-${r.id}`}
                              >
                                <Trash size={14} />
                                {isAr ? 'إلغاء' : 'Cancel'}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* New request dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent dir={isAr ? 'rtl' : 'ltr'} className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6]">
              {isAr ? 'طلب تصحيح بصمة' : 'New Correction Request'}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {isAr
                ? 'سيُرسل الطلب إلى مدير النظام للاعتماد. يمكنك التصحيح لآخر 7 أيام فقط.'
                : 'Will be sent to the System Administrator. Only last 7 days are allowed.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">{isAr ? 'تاريخ اليوم المراد تصحيحه' : 'Target Date'}</Label>
              <Input
                type="date"
                value={form.target_date}
                min={daysAgoLocal(7)}
                max={todayLocal()}
                onChange={(e) => setForm(p => ({ ...p, target_date: e.target.value }))}
                data-testid="input-target-date"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{isAr ? 'نوع التصحيح' : 'Correction Type'}</Label>
              <Select
                value={form.correction_type}
                onValueChange={(v) => setForm(p => ({ ...p, correction_type: v }))}
                dir={isAr ? 'rtl' : 'ltr'}
              >
                <SelectTrigger data-testid="input-correction-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="clock_in">{isAr ? 'تصحيح حضور (نسيان تسجيل الحضور)' : 'Clock-in correction'}</SelectItem>
                  <SelectItem value="clock_out">{isAr ? 'تصحيح انصراف (نسيان تسجيل الانصراف)' : 'Clock-out correction'}</SelectItem>
                  <SelectItem value="full_day">{isAr ? 'إضافة يوم كامل (لم أبصم نهائياً)' : 'Full day (no clock at all)'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(form.correction_type === 'clock_in' || form.correction_type === 'full_day') && (
              <div className="space-y-1">
                <Label className="text-xs">{isAr ? 'وقت الحضور المطلوب' : 'Requested clock-in'}</Label>
                <Input
                  type="time" lang="en-GB" dir="ltr"
                  value={form.requested_clock_in}
                  onChange={(e) => setForm(p => ({ ...p, requested_clock_in: e.target.value }))}
                  data-testid="input-clock-in"
                />
              </div>
            )}
            {(form.correction_type === 'clock_out' || form.correction_type === 'full_day') && (
              <div className="space-y-1">
                <Label className="text-xs">{isAr ? 'وقت الانصراف المطلوب' : 'Requested clock-out'}</Label>
                <Input
                  type="time" lang="en-GB" dir="ltr"
                  value={form.requested_clock_out}
                  onChange={(e) => setForm(p => ({ ...p, requested_clock_out: e.target.value }))}
                  data-testid="input-clock-out"
                />
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs">{isAr ? 'السبب' : 'Reason'}</Label>
              <Select
                value={form.reason_code}
                onValueChange={(v) => setForm(p => ({ ...p, reason_code: v }))}
                dir={isAr ? 'rtl' : 'ltr'}
              >
                <SelectTrigger data-testid="input-reason-code"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REASON_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>
                      {isAr ? o.label_ar : o.label_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{isAr ? 'شرح إضافي' : 'Additional explanation'}</Label>
              <Textarea
                value={form.reason_text}
                onChange={(e) => setForm(p => ({ ...p, reason_text: e.target.value }))}
                placeholder={isAr ? 'اكتب السبب بالتفصيل (5 أحرف على الأقل)' : 'Describe in detail (min 5 chars)'}
                rows={3}
                data-testid="input-reason-text"
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setOpenNew(false)} data-testid="cancel-new-btn">
              {isAr ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#655dc6] hover:bg-[#544cb0] text-white"
              data-testid="submit-correction-btn"
            >
              {submitting ? (isAr ? 'جارٍ الإرسال...' : 'Submitting...') : (isAr ? 'إرسال الطلب' : 'Submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
